import { Link } from "react-router-dom";
import { Star, Heart, Eye, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import { useState } from "react";
import QuickViewModal from "@/components/QuickViewModal";

export default function ProductCard({ product }) {
  const { addItem, toggleWishlist, wishlist } = useCart();
  const [quickOpen, setQuickOpen] = useState(false);
  const isWish = wishlist.includes(product.id);
  const discount = Math.round(((product.original_price - product.price) / product.original_price) * 100);

  return (
    <>
      <div className="product-card group relative" data-testid={`product-card-${product.id}`}>
        <div className="product-img-wrap relative aspect-[4/5]">
          <Link to={`/product/${product.id}`}>
            <img src={product.image} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </Link>

          {discount > 0 && (
            <div className="absolute top-3 left-3 bg-black text-white text-[10px] tracking-[0.3em] uppercase px-2.5 py-1" data-testid={`product-badge-${product.id}`}>
              -{discount}%
            </div>
          )}

          <button
            onClick={() => toggleWishlist(product.id)}
            className={`absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center transition-all ${
              isWish ? "text-[#C9A961]" : "text-black/70 hover:text-[#C9A961]"
            }`}
            data-testid={`wishlist-button-${product.id}`}
            aria-label="Wishlist"
          >
            <Heart size={15} fill={isWish ? "#C9A961" : "transparent"} strokeWidth={1.4} />
          </button>

          {/* Desktop hover overlay — Quick view only (Add to Bag moved below the image) */}
          <div className="hidden md:flex absolute inset-x-3 bottom-3 items-center gap-2 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
            <button
              onClick={() => addItem(product)}
              className="flex-1 bg-black text-white text-[11px] tracking-[0.22em] uppercase py-3 hover:bg-[#C9A961] transition-colors flex items-center justify-center gap-2"
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingBag size={13} /> Add to Bag
            </button>
            <button
              onClick={() => setQuickOpen(true)}
              className="w-11 h-11 bg-white border border-black/15 flex items-center justify-center hover:bg-[#F8F5F0] transition-colors"
              data-testid={`quick-view-${product.id}`}
              aria-label="Quick View"
            >
              <Eye size={14} strokeWidth={1.4} />
            </button>
          </div>
        </div>

        <div className="pt-4 pb-2">
          <div className="overline text-[10px] mb-1.5">{product.category}</div>
          <Link to={`/product/${product.id}`}>
            <h3 className="font-serif-display text-lg md:text-xl leading-snug line-clamp-2 hover:text-[#C9A961] transition-colors" data-testid={`product-name-${product.id}`}>
              {product.name}
            </h3>
          </Link>
          <div className="flex items-center gap-1 mt-1.5" data-testid={`product-rating-${product.id}`}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                size={11}
                fill={i <= Math.round(product.rating) ? "#C9A961" : "transparent"}
                strokeWidth={1.2}
                color="#C9A961"
              />
            ))}
            <span className="text-[11px] text-[#5C5C5C] ml-1">({product.review_count})</span>
          </div>
          <div className="flex items-baseline gap-2 mt-2" data-testid={`product-price-${product.id}`}>
            <span className="font-medium text-base">{formatINR(product.price)}</span>
            {product.original_price > product.price && (
              <span className="text-[#5C5C5C] line-through text-sm">{formatINR(product.original_price)}</span>
            )}
          </div>

          {/* Mobile/Always-visible Add to Bag below image — desktop also shows it for accessibility */}
          <button
            onClick={() => addItem(product)}
            className="md:hidden w-full mt-3 bg-black text-white text-[11px] tracking-[0.22em] uppercase py-3 active:bg-[#C9A961] transition-colors flex items-center justify-center gap-2"
            data-testid={`mobile-add-to-cart-${product.id}`}
          >
            <ShoppingBag size={13} /> Add to Bag
          </button>
        </div>
      </div>
      <QuickViewModal product={product} open={quickOpen} onClose={() => setQuickOpen(false)} />
    </>
  );
}
